import { trackGa4GenerateLead } from './funnelAnalytics';
import { measureSavedInquiry } from "./openAiConversionService";
import { metaMeasurementContext, measureMetaSavedInquiry, measurePreviewEvent } from './metaConversionService';
import { readMarketingExposures } from "./marketingExperimentService";
import { readMarketingAttribution } from "./marketingAttributionService";
import { requestDeadline } from "../utils/requestDeadline";
import type { FunnelSalesHandoff } from "../utils/funnelSalesHandoff";

const QUOTE_REQUEST_STORAGE_KEY = "bliss_funnel_quote_request_id";
const QUOTE_REQUEST_LOCAL_KEY = "bliss_funnel_quote_request_id";

export type FunnelLeadContact = {
  name?: string;
  email?: string;
  phone?: string;
  timeline?: string;
  projectZip?: string;
};

export type FunnelLeadSyncPayload = {
  /** Carry the same project across Bliss subdomains without relying on browser storage. */
  quoteRequestId?: string;
  source?: string;
  skipEmail?: boolean;
  /** Explicit final quote request; independent of the earlier contact-save email. */
  notifyQuoteRequest?: boolean;
  stage: string;
  contact: FunnelLeadContact;
  summary?: string;
  projectContext?: Record<string, unknown>;
  pricingEstimate?: {
    low?: number;
    high?: number;
    configLabel?: string;
  };
  /** Funnel answers translated into the columns the sales quote builder reads. */
  salesHandoff?: FunnelSalesHandoff;
  /** First capture creates lead + inbox row; progress updates only patch quote_requests */
  isProgressUpdate?: boolean;
};

export function getStoredFunnelQuoteRequestId(): string | null {
  try {
    return (
      sessionStorage.getItem(QUOTE_REQUEST_STORAGE_KEY) ||
      localStorage.getItem(QUOTE_REQUEST_LOCAL_KEY)
    );
  } catch {
    return null;
  }
}

export function storeFunnelQuoteRequestId(id: string) {
  try {
    sessionStorage.setItem(QUOTE_REQUEST_STORAGE_KEY, id);
    localStorage.setItem(QUOTE_REQUEST_LOCAL_KEY, id);
  } catch {
    /* ignore */
  }
}

export function blissProposalApprovalUrl(quoteRequestId: string): string {
  const id = quoteRequestId.trim();
  return `https://quote.blissbunkbeds.com/quote-approve/${encodeURIComponent(id)}`;
}

export type FunnelEstimateSnapshot = {
  preparedFor?: string;
  clientEmail?: string;
  clientPhone?: string;
  projectZip?: string;
  configLabel: string;
  priceLow: number;
  priceHigh: number;
  reserveBaseline?: number;
  sleeps?: number;
  demographicLabel?: string;
  accessLabel?: string;
  timelineLabel?: string;
  unitBreakdown?: string;
  isAsap?: boolean;
};

/** Persist (or refresh) a quote_requests row so email links open /quote-approve/:id, not the funnel gate. */
export async function ensureFunnelQuoteRequestForEstimate(
  estimate: FunnelEstimateSnapshot,
): Promise<string | undefined> {
  const name = estimate.preparedFor?.trim();
  if (!name) {
    return getStoredFunnelQuoteRequestId() || undefined;
  }

  const existing = getStoredFunnelQuoteRequestId();
  const result = await syncFunnelLeadToSalesInbox({
    stage: "estimate_shared",
    contact: {
      name,
      email: estimate.clientEmail,
      phone: estimate.clientPhone,
      projectZip: estimate.projectZip,
    },
    summary: [
      estimate.configLabel,
      estimate.unitBreakdown,
      `Estimate ${estimate.priceLow}-${estimate.priceHigh}`,
    ]
      .filter(Boolean)
      .join(" · "),
    projectContext: {
      funnelEstimate: estimate,
      lastSharedAt: new Date().toISOString(),
    },
    pricingEstimate: {
      low: estimate.priceLow,
      high: estimate.priceHigh,
      configLabel: estimate.configLabel,
    },
    isProgressUpdate: Boolean(existing),
  });

  return result.quoteRequestId || existing || undefined;
}

export async function syncFunnelLeadToSalesInbox(
  payload: FunnelLeadSyncPayload,
): Promise<{ success: boolean; quoteRequestId?: string; error?: string }> {
  const name = payload.contact.name?.trim();
  if (!name) {
    return { success: false, error: "Name is required to sync funnel lead" };
  }

  const attribution = readMarketingAttribution();
  const quoteRequestId =
    payload.quoteRequestId || getStoredFunnelQuoteRequestId();
  const isUpdate = Boolean(
    payload.quoteRequestId || (payload.isProgressUpdate && quoteRequestId),
  );
  const retryKey = "bliss_lead_capture_retry";
  let captureRequestId: string | undefined;
  if (!isUpdate) {
    try {
      captureRequestId =
        sessionStorage.getItem(retryKey) || crypto.randomUUID();
      sessionStorage.setItem(retryKey, captureRequestId);
    } catch {
      captureRequestId = crypto.randomUUID();
    }
  }

  try {
    return await requestDeadline(async (signal) => {
      const response = await fetch("/api/capture-lead", {
        signal,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          captureRequestId,
          marketingTouch: attribution.lastTouch,
          marketingFirstTouch: attribution.firstTouch,
          marketingExperiments: readMarketingExposures(),
          email: payload.contact.email?.trim() || undefined,
          phone: payload.contact.phone?.trim() || undefined,
          projectZip: payload.contact.projectZip?.trim() || undefined,
          timeline: payload.contact.timeline?.trim() || undefined,
          source: payload.source || "bliss_design_funnel",
          notes: payload.summary?.slice(0, 1000),
          tags: ["design_funnel", payload.stage],
          tenantSlug: "bliss-bedrooms",
          quoteRequestId: isUpdate ? quoteRequestId : undefined,
          funnelStage: payload.stage,
          projectContext: { ...payload.projectContext, metaMeasurement: metaMeasurementContext() },
          pricingEstimate: payload.pricingEstimate,
          salesHandoff: payload.salesHandoff,
          progressOnly: isUpdate,
          skipEmail: isUpdate || payload.skipEmail === true,
          notifyQuoteRequest: payload.notifyQuoteRequest === true && payload.skipEmail !== true,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success || !result.quote_request_id) {
        return { success: false, error: result.error || "Sync failed" };
      }

      signal.throwIfAborted();
      const id = result.quote_request_id as string | undefined;
      if (id) {
        storeFunnelQuoteRequestId(id);
        if (payload.notifyQuoteRequest === true && payload.skipEmail !== true && result.measurementEligible === true) {
          trackGa4GenerateLead(id, payload.source || 'bliss_design_funnel');
        }
        if (!isUpdate && result.measurementEligible === true) {
          measureSavedInquiry(id); measureMetaSavedInquiry(id); measurePreviewEvent('inquiry_saved');
        }
        try {
          sessionStorage.removeItem(retryKey);
        } catch {
          /* optional storage */
        }
      }
      return {
        success: true,
        quoteRequestId: id || quoteRequestId || undefined,
      };
    });
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Sync failed",
    };
  }
}


